import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {environment} from '@src/environments/environment';
import {PricingPreviewRequest, PricingPreviewResponse} from '@app/interfaces/checkout/pricing-breakdown';

/**
 * Payment type for unified payment initialization
 */
export type PaymentType = 'product_package' | 'module_package';

/**
 * Request payload for unified payment initialization
 */
export interface PaymentInitializeRequest {
  type: PaymentType;
  // Product package fields
  productPackageUuid?: string;
  productPackageType?: string;
  // Module package fields
  modulePackageUuid?: string;
  hasSubscription?: boolean;
  modulePackagePeriod?: 'monthly' | 'yearly' | null;
  ref?: string;
  // Force new card authorization even if existing payment method exists
  forceNewCard?: boolean;
}

/**
 * Existing payment method information returned when user has a card on file
 */
export interface ExistingPaymentMethod {
  id: string;
  brand: string;
  last4: string;
  exp_month: number;
  exp_year: number;
}

/**
 * Response from unified payment initialization endpoint
 */
export interface PaymentInitializationResponse {
  type: PaymentType;
  requires_authorization: boolean;
  stripe_client_secret?: string;
  setup_intent_id?: string;
  status?: 'activated' | 'processing' | 'pending';
  activated?: boolean;
  existing_payment_method?: ExistingPaymentMethod;
  account_product_package_uuid?: string;
  account_module_package_uuid?: string;
  product_package_uuid?: string;
  product_package_name?: string;
  product_package_type?: string;
  module_package_uuid?: string;
  module_package_name?: string;
  is_upgrade?: boolean;
  old_module_name?: string;
  message?: string;
  keycloak_refresh_token?: string;
  token_expired?: boolean;
  warning?: string;
}

/**
 * Request payload for activation status check
 */
export interface PaymentStatusRequest {
  type: PaymentType;
  setup_intent_id: string;
  ref?: string;
}

/**
 * Response from activation status check endpoint
 */
export interface PaymentStatusResponse {
  status: 'pending' | 'processing' | 'activated' | 'failed';
  account_product_package_uuid?: string;
  account_module_package_uuid?: string;
  product_package_name?: string;
  module_package_name?: string;
  message?: string;
  keycloak_refresh_token?: string;
  token_expired?: boolean;
  warning?: string;
}

/**
 * Unified Payment Service
 * Handles Stripe payment authorization for both product packages and module packages
 */
@Injectable({
  providedIn: 'root'
})
export class PaymentService {

  constructor(private http: HttpClient) {}

  /**
   * Initialize payment authorization
   * Creates Stripe SetupIntent and returns client secret for card authorization
   */
  initializePayment(request: PaymentInitializeRequest): Observable<PaymentInitializationResponse> {
    // Validate required fields based on payment type
    this.validatePaymentRequest(request);

    return this.http.post<PaymentInitializationResponse>(
      `${environment.apiUrl}/api/payments/initialize`,
      request
    );
  }

  /**
   * Validate payment request has required fields based on type
   * @throws Error if validation fails
   */
  private validatePaymentRequest(request: PaymentInitializeRequest): void {
    if (!request) {
      throw new Error('Payment request is required');
    }

    if (!request.type) {
      throw new Error('Payment type is required');
    }

    if (request.type === 'product_package') {
      if (!request.productPackageUuid) {
        throw new Error('productPackageUuid is required for product_package type');
      }
      if (!request.productPackageType) {
        throw new Error('productPackageType is required for product_package type');
      }
      // Validate UUID format
      if (!this.isValidUuid(request.productPackageUuid)) {
        throw new Error('Invalid productPackageUuid format');
      }
    }

    if (request.type === 'module_package') {
      if (!request.modulePackageUuid) {
        throw new Error('modulePackageUuid is required for module_package type');
      }
      // Validate UUID format
      if (!this.isValidUuid(request.modulePackageUuid)) {
        throw new Error('Invalid modulePackageUuid format');
      }
    }
  }

  /**
   * Validate UUID format
   */
  private isValidUuid(uuid: string): boolean {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return uuidRegex.test(uuid);
  }

  /**
   * Initialize payment for product package
   */
  initializeProductPackagePayment(
    productPackageUuid: string,
    productPackageType: string
  ): Observable<PaymentInitializationResponse> {
    return this.initializePayment({
      type: 'product_package',
      productPackageUuid,
      productPackageType
    });
  }

  /**
   * Initialize payment for module package
   */
  initializeModulePackagePayment(
    modulePackageUuid: string,
    hasSubscription: boolean = false,
    ref?: string,
    modulePackagePeriod?: 'monthly' | 'yearly' | null
  ): Observable<PaymentInitializationResponse> {
    const request: PaymentInitializeRequest = {
      type: 'module_package',
      modulePackageUuid,
      hasSubscription,
      modulePackagePeriod
    };
    if (ref) {
      request.ref = ref;
    }
    return this.initializePayment(request);
  }

  /**
   * Check payment/activation status after Stripe authorization
   * Used for polling after SetupIntent succeeds
   */
  checkStatus(request: PaymentStatusRequest): Observable<PaymentStatusResponse> {
    return this.http.post<PaymentStatusResponse>(
      `${environment.apiUrl}/api/payments/check-status`,
      request
    );
  }

  /**
   * Check activation status for product package
   */
  checkProductPackageStatus(setupIntentId: string): Observable<PaymentStatusResponse> {
    return this.checkStatus({
      type: 'product_package',
      setup_intent_id: setupIntentId
    });
  }

  /**
   * Check activation status for module package
   */
  checkModulePackageStatus(setupIntentId: string, ref?: string): Observable<PaymentStatusResponse> {
    const request: PaymentStatusRequest = {
      type: 'module_package',
      setup_intent_id: setupIntentId
    };
    if (ref) {
      request.ref = ref;
    }
    return this.checkStatus(request);
  }

  /**
   * Get Keycloak token for companion app redirect
   * Used when user doesn't have modulePackages feature flag
   */
  getCompanionAppToken(): Observable<{ success: boolean; keycloak_refresh_token?: string; token_expired?: boolean; message?: string }> {
    return this.http.get<{ success: boolean; keycloak_refresh_token?: string; token_expired?: boolean; message?: string }>(
      `${environment.apiUrl}/api/payments/companion-app-token`
    );
  }

  /**
   * Initialize payment for registration (before account creation)
   * For STARTER/PRO packages that require payment
   */
  initializeRegistrationPayment(registrationData: any): Observable<RegistrationPaymentResponse> {
    return this.http.post<RegistrationPaymentResponse>(
      `${environment.apiUrl}/api/demo/register/initialize-payment`,
      registrationData
    );
  }

  /**
   * Preview pricing breakdown for package change
   * Shows proration calculations, credits, and final amount before confirming
   */
  previewPricing(request: PricingPreviewRequest): Observable<PricingPreviewResponse> {
    return this.http.post<PricingPreviewResponse>(
      `${environment.apiUrl}/api/payments/preview`,
      request
    );
  }

  /**
   * Preview pricing for product package change
   */
  previewProductPackagePricing(
    productPackageUuid: string,
    productPackageType: string
  ): Observable<PricingPreviewResponse> {
    return this.previewPricing({
      type: 'product_package',
      productPackageUuid,
      productPackageType
    });
  }

  /**
   * Preview pricing for module package activation
   */
  previewModulePackagePricing(
    modulePackageUuid: string,
    modulePackagePeriod?: 'monthly' | 'yearly' | null
  ): Observable<PricingPreviewResponse> {
    return this.previewPricing({
      type: 'module_package',
      modulePackageUuid,
      modulePackagePeriod
    });
  }

  /**
   * Retry the invoice that locked the account for payment failure.
   * Returns 'succeeded' when Stripe accepts the charge (server clears the lock).
   * Returns 'failed' with refreshed error code/category for the lock screen.
   */
  retryFailedInvoice(): Observable<RetryFailedInvoiceResponse> {
    return this.http.post<RetryFailedInvoiceResponse>(
      `${environment.apiUrl}/api/payments/retry-failed-invoice`,
      {}
    );
  }

  /** The card currently on file, or null if none. */
  getCurrentPaymentMethod(): Observable<{ paymentMethod: ExistingPaymentMethod | null }> {
    return this.http.get<{ paymentMethod: ExistingPaymentMethod | null }>(
      `${environment.apiUrl}/api/payments/payment-method`
    );
  }

  createReplaceCardSetupIntent(): Observable<ReplaceCardSetupIntentResponse> {
    return this.http.post<ReplaceCardSetupIntentResponse>(
      `${environment.apiUrl}/api/payments/replace-card/setup-intent`,
      {}
    );
  }

  /** Save the newly-authorized card; server also retries any locked invoice. */
  confirmReplaceCard(setupIntentId: string): Observable<RetryFailedInvoiceResponse> {
    return this.http.post<RetryFailedInvoiceResponse>(
      `${environment.apiUrl}/api/payments/replace-card/confirm`,
      { setup_intent_id: setupIntentId }
    );
  }
}

export interface ReplaceCardSetupIntentResponse {
  stripe_client_secret: string;
  setup_intent_id: string;
}

export interface RetryFailedInvoiceResponse {
  status: 'succeeded' | 'failed' | 'no_failed_invoice' | 'missing_payment_method';
  code?: string | null;
  category?: 'issuer_decline' | 'recoverable' | 'unknown' | null;
  message?: string | null;
}

/**
 * Response from registration payment initialization
 */
export interface RegistrationPaymentResponse {
  success: boolean;
  requires_payment: boolean;
  message?: string;
  stripe?: {
    client_secret: string;
    setup_intent_id: string;
    publishable_key: string;
  };
}
